// src/pages/CreaTenant.js
'use strict';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Snackbar,
  Alert,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel,
  Divider,
} from '@mui/material';

// ───────────────────────────────────────────────────────────────
// Utils
// ───────────────────────────────────────────────────────────────
const normalize = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 20) || 'tenant';

const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());

function parseRutDv(input) {
  const raw = String(input || '')
    .trim()
    .toUpperCase()
    .replace(/\./g, '')
    .replace(/\s+/g, '');

  if (!raw) return { rut: '', dv: '' };

  // acepta 77629307-5 o 776293075 (último char DV)
  if (raw.includes('-')) {
    const [rut, dv] = raw.split('-');
    return { rut: (rut || '').replace(/\D/g, ''), dv: (dv || '').slice(0, 1) };
  }

  const m = raw.match(/^(\d+)([0-9K])$/);
  if (m) return { rut: m[1], dv: m[2] };

  if (/^\d+$/.test(raw)) return { rut: raw, dv: '' };

  return { rut: '', dv: '' };
}

function toISODate(s) {
  const v = String(s || '').trim();
  if (!v) return null;

  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;

  // dd/mm/yyyy
  const m = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) {
    const dd = String(m[1]).padStart(2, '0');
    const mm = String(m[2]).padStart(2, '0');
    const yy = m[3];
    return `${yy}-${mm}-${dd}`;
  }

  return null;
}

function parseAddressSmart(addr) {
  const s = String(addr || '').trim();
  if (!s) return { calle: '', numero: '', complemento: '' };

  // ejemplo: "Avenida Providencia 1208 Oficina 207"
  const m = s.match(/^(.*?)(?:\s+|,\s*)(\d+[A-Z0-9\-\/]*)\s*(.*)$/i);
  if (!m) return { calle: s, numero: '', complemento: '' };

  const calle = (m[1] || '').trim();
  const numero = (m[2] || '').trim();
  const complemento = (m[3] || '').trim();

  return { calle, numero, complemento };
}

// ───────────────────────────────────────────────────────────────
// Catálogos UI
// ───────────────────────────────────────────────────────────────
const COMPANY_TYPES = [
  { value: 'SpA', label: 'SpA' },
  { value: 'Ltda', label: 'Ltda' },
  { value: 'SA', label: 'SA' },
  { value: 'EIRL', label: 'EIRL' },
];

const AMBIENTES = [
  { value: 'Producción', label: 'Producción' },
  { value: 'Certificación', label: 'Certificación' },
  { value: 'Pruebas', label: 'Pruebas' },
];

const DIRECCION_TIPOS = [
  { value: 'Casa Matriz', label: 'Casa Matriz' },
  { value: 'Sucursal', label: 'Sucursal' },
  { value: 'Bodega', label: 'Bodega' },
  { value: 'Otro', label: 'Otro' },
];

// Seeds disponibles (deben coincidir con el backend)
const SEED_OPTIONS = [
  { key: 'generales', label: 'Datos generales' },
  { key: 'contabilidad', label: 'Datos contabilidad' },
  { key: 'rrhh', label: 'Datos RRHH' },
];

const steps = [
  'Datos de la Compañía',
  'Dirección & Contacto',
  'Representante Legal',
  'SII & Tributación',
  'Credenciales Admin',
  'Seleccionar BD Origen',
  'Resumen & Crear',
];

// ───────────────────────────────────────────────────────────────
// Form
// ───────────────────────────────────────────────────────────────
const emptyForm = {
  // base
  companyTaxId: '',
  razonSocialInput: '',
  nombre_comercial: '',
  tipo_empresa: 'SpA',
  giro: '',

  // contacto empresa
  companyEmail: '',
  telefono: '',
  sitio_web: '',
  correo_intercambio: '',
  email_boleta_electronica: '',
  email_ventas: '',

  // ubicación general
  pais: 'Chile',
  region: '',
  ciudad: '',
  comuna: '',
  codigo_postal: '',

  // dirección (empresa_direcciones)
  direccion_tipo: 'Casa Matriz',
  companyAddress: '',
  direccion_calle: '',
  direccion_numero: '',
  direccion_complemento: '',
  direccion_codigo_postal: '',
  direccion_telefono: '',
  direccion_email: '',
  direccion_ciudad: '',
  direccion_region: '',
  direccion_comuna: '',
  direccion_lat: '',
  direccion_lng: '',

  // representante
  representante_legal: '',
  rut_representante: '',
  correo_representante: '',
  telefono_representante: '',

  // sii / config
  regimen_tributario: '',
  gran_contribuyente: false,
  resolucion_exenta: '',
  fecha_resolucion: '',
  oficina_sii: '',
  codigo_actividad_principal: '',
  glosa_giro: '',
  capital_social: '',
  moneda: 'CLP',
  terminos_servicio_url: '',
  politica_privacidad_url: '',
  dias_corte_facturacion: '',
  ambiente_emision: 'Producción',
  codigos_actividad: '',
  notas: '',

  // admin
  adminPassword: '',
  adminPassword2: '',

  // origen
  srcDbName: '',
};

export default function CreaTenant() {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState({ ...emptyForm });

  const [dbList, setDbList] = useState([]);
  const [loadingDb, setLoadingDb] = useState(false);
  const [dbError, setDbError] = useState(false);

  const [creating, setCreating] = useState(false);
  const [createdInfo, setCreatedInfo] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Seeds selection
  const [applySeeds, setApplySeeds] = useState(true);
  const [seedKeys, setSeedKeys] = useState(SEED_OPTIONS.map((s) => s.key));

  const fetchDbList = useCallback(async () => {
    setLoadingDb(true);
    setDbError(false);
    try {
      const res = await api.get('/tenants/src-databases');
      setDbList(Array.isArray(res.data) ? res.data : []);
    } catch {
      setDbError(true);
      setDbList([]);
    } finally {
      setLoadingDb(false);
    }
  }, []);

  useEffect(() => {
    fetchDbList();
  }, [fetchDbList]);

  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));
  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSeedKeysChange = (e) => {
    const value = e.target.value;
    setSeedKeys(typeof value === 'string' ? value.split(',') : value);
  };

  const shortName = useMemo(() => normalize(form.razonSocialInput), [form.razonSocialInput]);

  // ── Auto defaults / helpers ───────────────────────────────────
  const rutParsed = useMemo(() => parseRutDv(form.companyTaxId), [form.companyTaxId]);

  // Autocompleta emails derivados (si están vacíos)
  useEffect(() => {
    const base = String(form.companyEmail || '').trim();
    if (!base || !isEmail(base)) return;

    setForm((prev) => {
      const next = { ...prev };
      let changed = false;

      if (!String(next.email_ventas || '').trim()) {
        next.email_ventas = base;
        changed = true;
      }
      if (!String(next.email_boleta_electronica || '').trim()) {
        next.email_boleta_electronica = base;
        changed = true;
      }
      if (!String(next.direccion_email || '').trim()) {
        next.direccion_email = base;
        changed = true;
      }

      return changed ? next : prev;
    });
  }, [form.companyEmail]);

  // Si escriben dirección libre y aún no separan calle/numero/complemento, intenta parsear
  useEffect(() => {
    const addr = String(form.companyAddress || '').trim();
    if (!addr) return;

    const calleEmpty = !String(form.direccion_calle || '').trim();
    const numEmpty = !String(form.direccion_numero || '').trim();
    const compEmpty = !String(form.direccion_complemento || '').trim();

    if (calleEmpty && numEmpty && compEmpty) {
      const p = parseAddressSmart(addr);
      setForm((prev) => ({
        ...prev,
        direccion_calle: p.calle || prev.direccion_calle,
        direccion_numero: p.numero || prev.direccion_numero,
        direccion_complemento: p.complemento || prev.direccion_complemento,
      }));
    }
  }, [form.companyAddress, form.direccion_calle, form.direccion_numero, form.direccion_complemento]);

  // ───────────────────────────────────────────────────────────────
  // Validación por paso
  // ───────────────────────────────────────────────────────────────
  const [touched, setTouched] = useState({});
  const markTouched = (name) => setTouched((t) => ({ ...t, [name]: true }));

  const errors = useMemo(() => {
    const e = {};

    // Step 0
    if (!String(form.companyTaxId || '').trim()) e.companyTaxId = 'RUT requerido (ej: 77629307-5)';
    else {
      const { rut, dv } = parseRutDv(form.companyTaxId);
      if (!rut || !dv) e.companyTaxId = 'RUT debe incluir DV (ej: 77629307-5)';
    }

    if (!String(form.razonSocialInput || '').trim()) e.razonSocialInput = 'Razón social requerida';
    if (!String(form.tipo_empresa || '').trim()) e.tipo_empresa = 'Tipo sociedad requerido';

    // Step 1
    if (activeStep >= 1) {
      if (!String(form.companyEmail || '').trim()) e.companyEmail = 'Email requerido';
      else if (!isEmail(form.companyEmail)) e.companyEmail = 'Email inválido';

      if (String(form.email_ventas || '').trim() && !isEmail(form.email_ventas)) e.email_ventas = 'Email inválido';
      if (String(form.email_boleta_electronica || '').trim() && !isEmail(form.email_boleta_electronica))
        e.email_boleta_electronica = 'Email inválido';

      if (!String(form.telefono || '').trim()) e.telefono = 'Teléfono requerido';

      const hasAddr = Boolean(String(form.direccion_calle || '').trim() || String(form.companyAddress || '').trim());
      if (!hasAddr) e.companyAddress = 'Dirección requerida';

      if (!String(form.direccion_tipo || '').trim()) e.direccion_tipo = 'Tipo dirección requerido';
      if (!String(form.direccion_calle || '').trim() && !String(form.companyAddress || '').trim())
        e.direccion_calle = 'Calle requerida';
    }

    // Step 2
    if (activeStep >= 2) {
      if (String(form.correo_representante || '').trim() && !isEmail(form.correo_representante)) {
        e.correo_representante = 'Email inválido';
      }
    }

    // Step 3
    if (activeStep >= 3) {
      if (!String(form.ambiente_emision || '').trim()) e.ambiente_emision = 'Ambiente requerido';

      const fr = String(form.fecha_resolucion || '').trim();
      if (fr && !toISODate(fr)) e.fecha_resolucion = 'Fecha inválida (dd/mm/aaaa o yyyy-mm-dd)';

      const fia = String(form.fecha_inicio_actividades || '').trim();
      if (fia && !toISODate(fia)) e.fecha_inicio_actividades = 'Fecha inválida (dd/mm/aaaa o yyyy-mm-dd)';
    }

    // Step 4
    if (activeStep >= 4) {
      if (!String(form.adminPassword || '').trim()) e.adminPassword = 'Contraseña requerida';
      else if (String(form.adminPassword).length < 8) e.adminPassword = 'Mínimo 8 caracteres';

      if (!String(form.adminPassword2 || '').trim()) e.adminPassword2 = 'Confirma la contraseña';
      else if (form.adminPassword2 !== form.adminPassword) e.adminPassword2 = 'No coincide';
    }

    // Step 5
    if (activeStep >= 5) {
      if (!String(form.srcDbName || '').trim()) e.srcDbName = 'Selecciona una BD origen';
      if (applySeeds && (!seedKeys || seedKeys.length === 0))
        e.seedKeys = 'Selecciona al menos un set de datos o desactiva seeds';
    }

    return e;
  }, [form, activeStep, applySeeds, seedKeys]);

  const canGoNext = useMemo(() => {
    const has = (k) => Boolean(errors[k]);

    if (activeStep === 0) return !has('companyTaxId') && !has('razonSocialInput') && !has('tipo_empresa');
    if (activeStep === 1)
      return (
        !has('companyEmail') &&
        !has('telefono') &&
        !has('companyAddress') &&
        !has('direccion_tipo') &&
        !has('direccion_calle') &&
        !has('email_ventas') &&
        !has('email_boleta_electronica')
      );
    if (activeStep === 2) return !has('correo_representante');
    if (activeStep === 3) return !has('ambiente_emision') && !has('fecha_resolucion') && !has('fecha_inicio_actividades');
    if (activeStep === 4) return !has('adminPassword') && !has('adminPassword2');
    if (activeStep === 5) return !has('srcDbName') && !has('seedKeys');
    return true;
  }, [activeStep, errors]);

  const handleNext = () => {
    const touch = (names) => names.forEach(markTouched);

    if (activeStep === 0) touch(['companyTaxId', 'razonSocialInput', 'tipo_empresa']);
    if (activeStep === 1)
      touch(['companyEmail', 'telefono', 'companyAddress', 'direccion_tipo', 'direccion_calle', 'email_ventas', 'email_boleta_electronica']);
    if (activeStep === 2) touch(['correo_representante']);
    if (activeStep === 3) touch(['ambiente_emision', 'fecha_resolucion', 'fecha_inicio_actividades']);
    if (activeStep === 4) touch(['adminPassword', 'adminPassword2']);
    if (activeStep === 5) touch(['srcDbName']);

    if (!canGoNext) {
      setSnackbar({ open: true, message: 'Revisa los campos requeridos', severity: 'warning' });
      return;
    }

    setActiveStep((s) => Math.min(steps.length - 1, s + 1));
  };

  // ───────────────────────────────────────────────────────────────
  // Construcción de objetos para insertar:
  //  - empresa_configuracion
  //  - empresa_direcciones
  // ───────────────────────────────────────────────────────────────
  const empresaConfigPreview = useMemo(() => {
    const { rut, dv } = rutParsed;

    return {
      tipo_sociedad: form.tipo_empresa || 'SpA',
      rut: rut || null,
      dv: dv || null,
      razon_social: form.razonSocialInput || null,
      nombre_fantasia: form.nombre_comercial || form.razonSocialInput || null,

      fecha_inicio_actividades: toISODate(form.fecha_inicio_actividades) || null,
      regimen_tributario: form.regimen_tributario || null,
      gran_contribuyente: Boolean(form.gran_contribuyente),

      resolucion_exenta: form.resolucion_exenta || null,
      fecha_resolucion: toISODate(form.fecha_resolucion) || null,
      oficina_sii: form.oficina_sii || null,

      codigo_actividad_principal: form.codigo_actividad_principal || null,
      giro: form.giro || null,
      glosa_giro: form.glosa_giro || null,

      capital_social: String(form.capital_social || '').trim() ? Number(form.capital_social) : null,
      moneda: form.moneda || 'CLP',

      region: form.region || null,
      ciudad: form.ciudad || null,
      comuna: form.comuna || null,
      codigo_postal: form.codigo_postal || null,

      telefono_principal: form.telefono || null,

      email_principal: form.companyEmail || null,
      email_boleta_electronica: form.email_boleta_electronica || form.companyEmail || null,
      website: form.sitio_web || null,

      terminos_servicio_url: form.terminos_servicio_url || null,
      politica_privacidad_url: form.politica_privacidad_url || null,
      dias_corte_facturacion: String(form.dias_corte_facturacion || '').trim()
        ? Number(form.dias_corte_facturacion)
        : null,

      email_ventas: form.email_ventas || form.companyEmail || null,
      ambiente_emision: form.ambiente_emision || 'Producción',
    };
  }, [form, rutParsed]);

  const empresaDireccionesPreview = useMemo(() => {
    const calle = String(form.direccion_calle || '').trim() || String(form.companyAddress || '').trim() || '';
    return [
      {
        tipo: form.direccion_tipo || 'Casa Matriz',
        calle,
        numero: String(form.direccion_numero || '').trim() || null,
        comuna: form.direccion_comuna || form.comuna || null,
        region: form.direccion_region || form.region || null,
        codigo_postal: form.direccion_codigo_postal || form.codigo_postal || null,
        lat: String(form.direccion_lat || '').trim() ? Number(form.direccion_lat) : null,
        lng: String(form.direccion_lng || '').trim() ? Number(form.direccion_lng) : null,
        telefono: form.direccion_telefono || form.telefono || null,
        email: form.direccion_email || form.email_ventas || form.companyEmail || null,
        complemento: String(form.direccion_complemento || '').trim() || null,
        ciudad: form.direccion_ciudad || form.ciudad || null,
      },
    ];
  }, [form]);

  // ───────────────────────────────────────────────────────────────
  // Crear tenant
  // ───────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    setCreating(true);
    setCreatedInfo(null);

    try {
      const payload = {
        razonSocialInput: form.razonSocialInput,
        companyTaxId: form.companyTaxId,
        companyAddress: form.companyAddress,
        companyEmail: form.companyEmail,
        telefono: form.telefono,

        sitio_web: form.sitio_web,
        correo_intercambio: form.correo_intercambio,

        nombre_comercial: form.nombre_comercial,
        tipo_empresa: form.tipo_empresa,
        giro: form.giro,
        comuna: form.comuna,
        ciudad: form.ciudad,
        region: form.region,
        pais: form.pais,
        codigos_actividad: form.codigos_actividad,
        notas: form.notas,

        representante_legal: form.representante_legal,
        rut_representante: form.rut_representante,
        correo_representante: form.correo_representante,
        telefono_representante: form.telefono_representante,

        resolucion_facturacion_electronica: form.resolucion_facturacion_electronica,
        fecha_resolucion: form.fecha_resolucion,
        fecha_inicio_actividades: form.fecha_inicio_actividades,
        fecha_constitucion: form.fecha_constitucion,
        estado: form.estado,

        adminPassword: form.adminPassword,

        srcDb: form.srcDbName,

        apply_seeds: applySeeds,
        seed_keys: applySeeds ? seedKeys : [],

        // NUEVO: para insertar en tablas del tenant
        empresa_configuracion: empresaConfigPreview,
        empresa_direcciones: empresaDireccionesPreview,
      };

      const res = await api.post('/tenants/create', payload);

      setCreatedInfo(res.data || null);
      setSnackbar({ open: true, message: 'Tenant creado exitosamente', severity: 'success' });
      setActiveStep(6);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Error creando tenant';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const resetAll = () => {
    setForm({ ...emptyForm });
    setTouched({});
    setActiveStep(0);
    setCreatedInfo(null);
    setApplySeeds(true);
    setSeedKeys(SEED_OPTIONS.map((s) => s.key));
  };

  // ───────────────────────────────────────────────────────────────
  // UI helpers
  // ───────────────────────────────────────────────────────────────
  const tf = (name) => ({
    name,
    value: form[name] ?? '',
    onChange: handleChange,
    onBlur: () => markTouched(name),
    error: Boolean(touched[name] && errors[name]),
    helperText: touched[name] && errors[name] ? errors[name] : ' ',
    fullWidth: true,
    size: 'small',
  });

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField label="RUT (con DV) ej: 77629307-5" {...tf('companyTaxId')} />
              <Box mt={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Detectado: RUT <b>{rutParsed.rut || '—'}</b> / DV <b>{rutParsed.dv || '—'}</b>
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label="Razón Social" {...tf('razonSocialInput')} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label="Nombre Fantasía / Comercial" {...tf('nombre_comercial')} />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" error={Boolean(touched.tipo_empresa && errors.tipo_empresa)}>
                <InputLabel>Tipo Sociedad</InputLabel>
                <Select
                  label="Tipo Sociedad"
                  name="tipo_empresa"
                  value={form.tipo_empresa}
                  onChange={handleChange}
                  onBlur={() => markTouched('tipo_empresa')}
                >
                  {COMPANY_TYPES.map((x) => (
                    <MenuItem key={x.value} value={x.value}>
                      {x.label}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="error" sx={{ minHeight: 18, display: 'block', mt: 0.5 }}>
                  {touched.tipo_empresa && errors.tipo_empresa ? errors.tipo_empresa : ' '}
                </Typography>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField label="Giro (actividad)" {...tf('giro')} />
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Identificador base</Typography>
                <Typography variant="body2">
                  Se generará automáticamente como: <b>tenant_{shortName}_&lt;timestamp&gt;</b>
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Contacto</Typography>

                <Grid container spacing={2} mt={0.5}>
                  <Grid item xs={12} md={6}>
                    <TextField label="Email empresa" {...tf('companyEmail')} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Teléfono" {...tf('telefono')} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Email ventas (empresa_configuracion.email_ventas)" {...tf('email_ventas')} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Email boleta electrónica (empresa_configuracion.email_boleta_electronica)"
                      {...tf('email_boleta_electronica')}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Sitio web" {...tf('sitio_web')} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Correo intercambio (opcional)" {...tf('correo_intercambio')} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Dirección (empresa_direcciones)</Typography>

                <Grid container spacing={2} mt={0.5}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" error={Boolean(touched.direccion_tipo && errors.direccion_tipo)}>
                      <InputLabel>Tipo</InputLabel>
                      <Select
                        label="Tipo"
                        name="direccion_tipo"
                        value={form.direccion_tipo}
                        onChange={handleChange}
                        onBlur={() => markTouched('direccion_tipo')}
                      >
                        {DIRECCION_TIPOS.map((x) => (
                          <MenuItem key={x.value} value={x.value}>
                            {x.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="caption" color="error" sx={{ minHeight: 18, display: 'block', mt: 0.5 }}>
                        {touched.direccion_tipo && errors.direccion_tipo ? errors.direccion_tipo : ' '}
                      </Typography>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <TextField label="Dirección (texto libre) — opcional si completas Calle" {...tf('companyAddress')} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Calle" {...tf('direccion_calle')} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField label="Número" {...tf('direccion_numero')} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField label="Complemento" {...tf('direccion_complemento')} />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField label="Comuna" {...tf('comuna')} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="Ciudad" {...tf('ciudad')} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="Región" {...tf('region')} />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField label="Código Postal" {...tf('codigo_postal')} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="País" {...tf('pais')} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField label="Dirección: Código Postal (override)" {...tf('direccion_codigo_postal')} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Dirección: Teléfono (override)" {...tf('direccion_telefono')} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Dirección: Email (override)" {...tf('direccion_email')} />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField label="Lat (opcional)" {...tf('direccion_lat')} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Lng (opcional)" {...tf('direccion_lng')} />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="caption" color="error" sx={{ minHeight: 18, display: 'block' }}>
                      {touched.companyAddress && errors.companyAddress ? errors.companyAddress : ' '}
                    </Typography>
                    <Typography variant="caption" color="error" sx={{ minHeight: 18, display: 'block' }}>
                      {touched.direccion_calle && errors.direccion_calle ? errors.direccion_calle : ' '}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField label="Representante Legal" {...tf('representante_legal')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="RUT Representante" {...tf('rut_representante')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Correo Representante" {...tf('correo_representante')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Teléfono Representante" {...tf('telefono_representante')} />
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField label="Regimen tributario (opcional)" {...tf('regimen_tributario')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" error={Boolean(touched.ambiente_emision && errors.ambiente_emision)}>
                <InputLabel>Ambiente emisión</InputLabel>
                <Select
                  label="Ambiente emisión"
                  name="ambiente_emision"
                  value={form.ambiente_emision}
                  onChange={handleChange}
                  onBlur={() => markTouched('ambiente_emision')}
                >
                  {AMBIENTES.map((x) => (
                    <MenuItem key={x.value} value={x.value}>
                      {x.label}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="error" sx={{ minHeight: 18, display: 'block', mt: 0.5 }}>
                  {touched.ambiente_emision && errors.ambiente_emision ? errors.ambiente_emision : ' '}
                </Typography>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label="Resolución exenta (número)" {...tf('resolucion_exenta')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Fecha resolución (dd/mm/aaaa o yyyy-mm-dd)" {...tf('fecha_resolucion')} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label="Oficina SII" {...tf('oficina_sii')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Código actividad principal (SII)" {...tf('codigo_actividad_principal')} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label="Inicio actividades (dd/mm/aaaa o yyyy-mm-dd)" {...tf('fecha_inicio_actividades')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Constitución (opcional)" {...tf('fecha_constitucion')} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label="Glosa giro (opcional)" {...tf('glosa_giro')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Capital social (opcional)" {...tf('capital_social')} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label="Moneda (CLP)" {...tf('moneda')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Días corte facturación (opcional)" {...tf('dias_corte_facturacion')} />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.gran_contribuyente)}
                    onChange={(e) => setForm((f) => ({ ...f, gran_contribuyente: e.target.checked }))}
                  />
                }
                label="Gran contribuyente"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label="Términos servicio URL (opcional)" {...tf('terminos_servicio_url')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Política privacidad URL (opcional)" {...tf('politica_privacidad_url')} />
            </Grid>

            <Grid item xs={12}>
              <TextField label="Códigos actividad (opcional)" {...tf('codigos_actividad')} />
            </Grid>

            <Grid item xs={12}>
              <TextField label="Notas (opcional)" {...tf('notas')} multiline minRows={3} />
            </Grid>
          </Grid>
        );

      case 4:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body2">
                  El admin se creará con el email de empresa:
                  <b> {form.companyEmail || '(sin email aún)'} </b>
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label="Contraseña Admin" type="password" {...tf('adminPassword')} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Confirmar contraseña" type="password" {...tf('adminPassword2')} />
            </Grid>
          </Grid>
        );

      case 5:
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {loadingDb && <CircularProgress />}
              {dbError && (
                <Box textAlign="center" mt={2}>
                  <Typography color="error">Error cargando bases de datos</Typography>
                  <Button onClick={fetchDbList} sx={{ mt: 1 }}>
                    Reintentar
                  </Button>
                </Box>
              )}

              {!loadingDb && !dbError && (
                <FormControl fullWidth size="small" error={Boolean(touched.srcDbName && errors.srcDbName)}>
                  <InputLabel>Base de datos origen</InputLabel>
                  <Select
                    value={form.srcDbName}
                    name="srcDbName"
                    onChange={handleChange}
                    onBlur={() => markTouched('srcDbName')}
                    input={<OutlinedInput label="Base de datos origen" />}
                  >
                    <MenuItem value="" disabled>
                      Selecciona base de datos origen…
                    </MenuItem>
                    {dbList.map((db) => (
                      <MenuItem key={db} value={db}>
                        {db}
                      </MenuItem>
                    ))}
                  </Select>

                  <Typography variant="caption" color="error" sx={{ minHeight: 18, display: 'block', mt: 0.5 }}>
                    {touched.srcDbName && errors.srcDbName ? errors.srcDbName : ' '}
                  </Typography>
                </FormControl>
              )}
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2">Datos iniciales (Seeds)</Typography>
                <Box display="flex" alignItems="center" mt={1} gap={2} flexWrap="wrap">
                  <Button variant={applySeeds ? 'contained' : 'outlined'} onClick={() => setApplySeeds(true)} size="small">
                    Aplicar seeds
                  </Button>
                  <Button variant={!applySeeds ? 'contained' : 'outlined'} onClick={() => setApplySeeds(false)} size="small">
                    No aplicar
                  </Button>
                </Box>

                {applySeeds && (
                  <Box mt={2}>
                    <FormControl fullWidth size="small" error={Boolean(errors.seedKeys)}>
                      <InputLabel>Sets de datos</InputLabel>
                      <Select
                        multiple
                        value={seedKeys}
                        onChange={handleSeedKeysChange}
                        input={<OutlinedInput label="Sets de datos" />}
                        renderValue={(selected) =>
                          selected.map((k) => SEED_OPTIONS.find((x) => x.key === k)?.label || k).join(', ')
                        }
                      >
                        {SEED_OPTIONS.map((opt) => (
                          <MenuItem key={opt.key} value={opt.key}>
                            <Checkbox checked={seedKeys.indexOf(opt.key) > -1} />
                            <ListItemText primary={opt.label} />
                          </MenuItem>
                        ))}
                      </Select>

                      <Typography variant="caption" color="error" sx={{ minHeight: 18, display: 'block', mt: 0.5 }}>
                        {errors.seedKeys ? errors.seedKeys : ' '}
                      </Typography>
                    </FormControl>

                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                      El backend insertará solo los datos definidos en los JSON (sin copiar datos del origen) y resincronizará secuencias.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        );

      case 6: {
        const summary = (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1">Resumen</Typography>

            <Box mt={1}>
              <Typography>
                Empresa: <b>{form.razonSocialInput || '-'}</b> ({form.companyTaxId || '-'})
              </Typography>
              <Typography>
                Email: <b>{form.companyEmail || '-'}</b>
              </Typography>
              <Typography>
                BD Origen: <b>{form.srcDbName || '-'}</b>
              </Typography>
              <Typography>
                Seeds: <b>{applySeeds ? 'Sí' : 'No'}</b>
                {applySeeds ? ` (${seedKeys.join(', ')})` : ''}
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2">empresa_configuracion (preview)</Typography>
            <Box mt={1}>
              <Typography variant="body2">tipo_sociedad: <b>{empresaConfigPreview.tipo_sociedad || '-'}</b></Typography>
              <Typography variant="body2">
                rut: <b>{empresaConfigPreview.rut || '-'}</b> / dv: <b>{empresaConfigPreview.dv || '-'}</b>
              </Typography>
              <Typography variant="body2">razon_social: <b>{empresaConfigPreview.razon_social || '-'}</b></Typography>
              <Typography variant="body2">nombre_fantasia: <b>{empresaConfigPreview.nombre_fantasia || '-'}</b></Typography>
              <Typography variant="body2">ambiente_emision: <b>{empresaConfigPreview.ambiente_emision || '-'}</b></Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2">empresa_direcciones (preview)</Typography>
            <Box mt={1}>
              <Typography variant="body2">tipo: <b>{empresaDireccionesPreview?.[0]?.tipo || '-'}</b></Typography>
              <Typography variant="body2">
                calle: <b>{empresaDireccionesPreview?.[0]?.calle || '-'}</b> nro: <b>{empresaDireccionesPreview?.[0]?.numero || '-'}</b>
              </Typography>
              <Typography variant="body2">
                comuna/ciudad/región:{' '}
                <b>{empresaDireccionesPreview?.[0]?.comuna || '-'}</b> / <b>{empresaDireccionesPreview?.[0]?.ciudad || '-'}</b> /{' '}
                <b>{empresaDireccionesPreview?.[0]?.region || '-'}</b>
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
              El backend debe consumir <b>empresa_configuracion</b> y <b>empresa_direcciones</b> del payload para insertar en esas tablas.
            </Typography>
          </Paper>
        );

        const result = createdInfo ? (
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle1">Resultado</Typography>
            <Box mt={1}>
              <Typography>Tenant ID: <b>{createdInfo.tenantId ?? '-'}</b></Typography>
              <Typography>BD creada: <b>{createdInfo.db_name ?? '-'}</b></Typography>
              <Typography>Usuario BD: <b>{createdInfo.db_user ?? '-'}</b></Typography>

              {createdInfo.applied && (
                <Box mt={1}>
                  <Typography variant="subtitle2">Acciones aplicadas</Typography>
                  <Typography variant="body2">• Esquema incremental: <b>{createdInfo.applied.schema_incremental ? 'Sí' : 'No'}</b></Typography>
                  <Typography variant="body2">• UNIQUE configuraciones.clave: <b>{createdInfo.applied.unique_configuraciones_clave ? 'Sí' : 'No'}</b></Typography>
                  <Typography variant="body2">• Secuencias resincronizadas: <b>{createdInfo.applied.sequences_resynced ? 'Sí' : 'No'}</b></Typography>
                  <Typography variant="body2">• Seeds aplicados: <b>{createdInfo.applied.seeds_applied ? 'Sí' : 'No'}</b></Typography>
                </Box>
              )}
            </Box>

            <Box mt={2} display="flex" gap={1} flexWrap="wrap">
              <Button variant="outlined" onClick={resetAll}>Crear otro tenant</Button>
            </Box>
          </Paper>
        ) : null;

        return (
          <>
            {summary}
            {result}
          </>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Crear Tenant (Wizard)
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Dialog open fullWidth maxWidth="md">
        <DialogTitle>{steps[activeStep]}</DialogTitle>
        <DialogContent dividers>{getStepContent(activeStep)}</DialogContent>
        <DialogActions>
          <Button disabled={activeStep === 0 || creating} onClick={handleBack}>
            Volver
          </Button>

          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={handleNext} disabled={creating || !canGoNext}>
              Siguiente
            </Button>
          ) : (
            <>
              <Button variant="outlined" disabled={creating} onClick={resetAll}>
                Reiniciar
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreate}
                disabled={creating || Boolean(createdInfo)}
              >
                {creating ? <CircularProgress size={24} /> : createdInfo ? 'Creado' : 'Crear Tenant'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4500} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
